import { CARWIZ_FIELDS, EDITABLE_FIELDS } from '../constants';

/**
 * Generate a stable ID for a row based on its DMV fields.
 * Uses dmv_make, dmv_model, and dmv_finish_level to create a composite key.
 * This ensures IDs remain stable even when rows are filtered or reordered.
 *
 * @param {Object} row - The row object
 * @param {number} [duplicateIndex] - Optional index to append for duplicate IDs
 * @returns {string} - A stable ID for the row
 */
export const generateStableRowId = (row, duplicateIndex = null) => {
  // Normalize and extract DMV fields
  const make = (row.dmv_make || '').trim().toLowerCase();
  const model = (row.dmv_model || '').trim().toLowerCase();
  const finishLevel = (row.dmv_finish_level || '').trim().toLowerCase();

  // Create composite key from DMV fields
  // Use empty string placeholder if field is missing to ensure uniqueness
  let compositeKey = `${make || '_empty_'}-${model || '_empty_'}-${
    finishLevel || '_empty_'
  }`;

  // If all fields are empty, fall back to a hash of the entire row
  // This handles edge cases where DMV fields might not be present
  if (make === '' && model === '' && finishLevel === '') {
    // Create a simple hash from the row's string representation
    const rowString = JSON.stringify(row);
    let hash = 0;
    for (let i = 0; i < rowString.length; i++) {
      const char = rowString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    compositeKey = `_hash_${Math.abs(hash)}`;
  }

  // Append duplicate index if provided (for handling duplicate DMV combinations)
  if (duplicateIndex !== null && duplicateIndex > 0) {
    compositeKey = `${compositeKey}_${duplicateIndex}`;
  }

  return compositeKey;
};

// Helper function to check if a row is missing any carwiz fields
export const isRowMissing = (row) => {
  return CARWIZ_FIELDS.some((field) => {
    const value = row[field];
    return !value || (typeof value === 'string' && value.trim() === '');
  });
};

// Helper function to check if a row has been edited
export const isRowChanged = (row, originalRow) => {
  if (!originalRow) return false;

  return EDITABLE_FIELDS.some((field) => {
    const currentValue = row[field] || '';
    const originalValue = originalRow[field] || '';
    // Normalize values for comparison (trim strings)
    const normalizedCurrent =
      typeof currentValue === 'string' ? currentValue.trim() : currentValue;
    const normalizedOriginal =
      typeof originalValue === 'string' ? originalValue.trim() : originalValue;
    return normalizedCurrent !== normalizedOriginal;
  });
};

